"use client";

import { useCallback, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Search, X, ArrowRight, Tag, Newspaper } from "lucide-react";
import { useRouter } from "@/i18n/navigation";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { $api } from "@/openapi/client-auth";
import { cn } from "@/lib/utils";

import { MIN_SEARCH_LENGTH } from "@donista/shared/constants/search";
const PREVIEW_SIZE = 3;

type Source = "shops" | "projects" | "products" | "discountCodes" | "releaseNotes";

interface AutocompleteSearchBarProps {
  placeholder?: string;
  seeAllLabel?: string;
  defaultValue?: string;
  onSubmit?: (term: string) => void;
  className?: string;
  sources?: Source[];
}

type DropdownItem =
  | { type: "shop"; id: string | undefined; name: string | undefined; logoUrl: string | null | undefined; slug: string | undefined }
  | { type: "project"; id: string | undefined; name: string | undefined; logoUrl: string | null | undefined; slug: string | undefined }
  | { type: "product"; id: string | undefined; name: string; logoUrl: string | null | undefined }
  | { type: "discountCode"; id: string | undefined; name: string | undefined; logoUrl: string | null | undefined; shopName: string | undefined }
  | { type: "releaseNote"; id: string | undefined; name: string | undefined; logoUrl: null; slug: string | undefined }
  | { type: "footer"; id: string; name: string; logoUrl: null; slug: string };

export function AutocompleteSearchBar({
  placeholder,
  seeAllLabel,
  defaultValue = "",
  onSubmit,
  className,
  sources = ["shops", "projects", "products", "discountCodes"],
}: AutocompleteSearchBarProps) {
  const router = useRouter();
  const t = useTranslations("search.page");
  const [query, setQuery] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = query.trim();
  const hasQuery = trimmed.length >= MIN_SEARCH_LENGTH;

  const showShops = sources.includes("shops");
  const showProjects = sources.includes("projects");
  const showProducts = sources.includes("products");
  const showDiscountCodes = sources.includes("discountCodes");
  const showReleaseNotes = sources.includes("releaseNotes");

  const { data: shopsData, isLoading: shopsLoading } = $api.useQuery(
    "get",
    "/shops/search",
    { params: { query: { term: trimmed, page: 0, size: PREVIEW_SIZE } } },
    { enabled: hasQuery && showShops, staleTime: 30_000 },
  );

  const { data: projectsData, isLoading: projectsLoading } = $api.useQuery(
    "get",
    "/projects/search",
    { params: { query: { searchTerm: trimmed, page: 0, size: PREVIEW_SIZE } } },
    { enabled: hasQuery && showProjects, staleTime: 30_000 },
  );

  const { data: productsData, isLoading: productsLoading } = $api.useQuery(
    "get",
    "/products/search",
    { params: { query: { q: trimmed, page: 0, size: PREVIEW_SIZE } } },
    { enabled: hasQuery && showProducts && trimmed.length >= 3, staleTime: 30_000 },
  );

  const { data: discountCodesData, isLoading: discountCodesLoading } = $api.useQuery(
    "get",
    "/discount-codes/latest",
    {},
    { enabled: hasQuery && showDiscountCodes, staleTime: 60_000 },
  );

  const { data: releaseNotesData, isLoading: releaseNotesLoading } = $api.useQuery(
    "get",
    "/release-notes",
    { params: { query: { search: trimmed, page: 0, size: PREVIEW_SIZE, sortBy: "publishedAt", sortDirection: "desc" } } },
    { enabled: hasQuery && showReleaseNotes, staleTime: 30_000 },
  );

  const shops = showShops ? (shopsData?.content ?? []) : [];
  const projects = showProjects ? (projectsData?.content ?? []) : [];
  const products = showProducts ? (productsData?.content ?? []) : [];
  const allDiscountCodes = showDiscountCodes ? (discountCodesData ?? []) : [];
  const discountCodes = allDiscountCodes
    .filter((dc: { title?: string; shopName?: string }) =>
      (dc.title?.toLowerCase().includes(trimmed.toLowerCase())) ||
      (dc.shopName?.toLowerCase().includes(trimmed.toLowerCase()))
    )
    .slice(0, PREVIEW_SIZE);
  const releaseNotes = showReleaseNotes ? (releaseNotesData?.content ?? []) : [];
  const isLoading = (showShops && shopsLoading) || (showProjects && projectsLoading) || (showProducts && productsLoading) || (showDiscountCodes && discountCodesLoading) || (showReleaseNotes && releaseNotesLoading);
  const hasResults = shops.length > 0 || projects.length > 0 || products.length > 0 || discountCodes.length > 0 || releaseNotes.length > 0;
  const showDropdown = open && hasQuery;

  const allItems: DropdownItem[] = [
    ...shops.map((s) => ({ type: "shop" as const, id: s.id, name: s.name, logoUrl: s.logoUrl, slug: s.slug })),
    ...projects.map((p) => ({ type: "project" as const, id: p.id, name: p.name, logoUrl: p.logoUrl, slug: p.slug })),
    ...products.map((p) => ({ type: "product" as const, id: p.productId, name: p.name, logoUrl: p.imageUrl })),
    ...discountCodes.map((dc) => ({ type: "discountCode" as const, id: dc.id, name: dc.title, logoUrl: dc.shopLogoUrl, shopName: dc.shopName })),
    ...releaseNotes.map((rn) => ({ type: "releaseNote" as const, id: rn.slug, name: rn.title, logoUrl: null, slug: rn.slug })),
    ...(hasResults ? [{ type: "footer" as const, id: "footer", name: "", logoUrl: null, slug: "" }] : []),
  ];

  const navigate = useCallback((href: string) => {
    setOpen(false);
    router.push(href);
  }, [router]);

  const handleSubmit = useCallback(() => {
    const term = query.trim();
    if (!term) return;
    setOpen(false);
    if (onSubmit) {
      onSubmit(term);
    } else {
      router.push(`/search?q=${encodeURIComponent(term)}`);
    }
  }, [query, onSubmit, router]);

  const handleItemSelect = (index: number) => {
    const item = allItems[index];
    if (!item) return;
    if (item.type === "footer") {
      handleSubmit();
    } else if (item.type === "shop" && "slug" in item) {
      navigate(`/shops/${item.slug}`);
    } else if (item.type === "project" && "slug" in item) {
      navigate(`/projects/${item.slug}`);
    } else if (item.type === "product") {
      handleSubmit();
    } else if (item.type === "discountCode") {
      handleSubmit();
    } else if (item.type === "releaseNote" && "slug" in item) {
      navigate(`/release-notes/${item.slug}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || !hasResults) {
      if (e.key === "Enter") handleSubmit();
      if (e.key === "Escape") setOpen(false);
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) => (prev < allItems.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : allItems.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0) {
          handleItemSelect(activeIndex);
        } else {
          handleSubmit();
        }
        break;
      case "Escape":
        setOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const resolvedPlaceholder = placeholder ?? t("searchPlaceholder");
  const resolvedSeeAll = seeAllLabel ?? t("seeAll");

  let runningIndex = 0;

  return (
    <div ref={containerRef} className={cn("relative", className)} onBlur={handleBlur}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={resolvedPlaceholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full pl-11 pr-10 bg-white dark:bg-input/30 border border-input rounded-full focus-visible:border-primary focus-visible:ring-primary/50 hover:shadow-md transition-all duration-200"
        />
        {query && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-2 top-1/2 h-7 w-7 -translate-y-1/2 p-0 hover:bg-transparent"
            onClick={() => {
              setQuery("");
              setOpen(false);
              setActiveIndex(-1);
              inputRef.current?.focus();
              onSubmit?.("");
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-popover border border-border rounded-2xl shadow-lg z-50 overflow-hidden">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 rounded-md" />
              ))}
            </div>
          ) : !hasResults ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              {t("noResults")}
            </div>
          ) : (
            <>
              {shops.length > 0 && (
                <DropdownSection label={t("shops")}>
                  {shops.map((shop) => {
                    const idx = runningIndex++;
                    return (
                      <DropdownRow
                        key={shop.id}
                        name={shop.name ?? ""}
                        logoUrl={shop.logoUrl}
                        active={activeIndex === idx}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => navigate(`/shops/${shop.slug}`)}
                      />
                    );
                  })}
                </DropdownSection>
              )}

              {projects.length > 0 && (
                <DropdownSection label={t("projects")} border={shops.length > 0}>
                  {projects.map((project) => {
                    const idx = runningIndex++;
                    return (
                      <DropdownRow
                        key={project.id}
                        name={project.name ?? ""}
                        logoUrl={project.logoUrl}
                        active={activeIndex === idx}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => navigate(`/projects/${project.slug}`)}
                      />
                    );
                  })}
                </DropdownSection>
              )}

              {products.length > 0 && (
                <DropdownSection label={t("products")} border={shops.length > 0 || projects.length > 0}>
                  {products.map((product) => {
                    const idx = runningIndex++;
                    return (
                      <DropdownRow
                        key={product.productId}
                        name={product.name}
                        logoUrl={product.imageUrl}
                        active={activeIndex === idx}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={handleSubmit}
                      />
                    );
                  })}
                </DropdownSection>
              )}

              {discountCodes.length > 0 && (
                <DropdownSection label={t("discountCodes")} border={shops.length > 0 || projects.length > 0 || products.length > 0}>
                  {discountCodes.map((dc) => {
                    const idx = runningIndex++;
                    return (
                      <DropdownRow
                        key={dc.id}
                        name={dc.title ?? ""}
                        subtitle={dc.shopName ?? undefined}
                        logoUrl={dc.shopLogoUrl}
                        icon={!dc.shopLogoUrl ? <Tag className="h-4 w-4 text-muted-foreground" /> : undefined}
                        active={activeIndex === idx}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={handleSubmit}
                      />
                    );
                  })}
                </DropdownSection>
              )}

              {releaseNotes.length > 0 && (
                <DropdownSection label="Release Notes" border={shops.length > 0 || projects.length > 0 || products.length > 0 || discountCodes.length > 0}>
                  {releaseNotes.map((rn) => {
                    const idx = runningIndex++;
                    return (
                      <DropdownRow
                        key={rn.slug}
                        name={rn.title ?? ""}
                        icon={<Newspaper className="h-4 w-4 text-muted-foreground" />}
                        active={activeIndex === idx}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => navigate(`/release-notes/${rn.slug}`)}
                      />
                    );
                  })}
                </DropdownSection>
              )}

              <button
                type="button"
                className={cn(
                  "w-full flex items-center justify-center gap-1 px-3 py-2.5 text-sm font-medium text-primary transition-colors border-t border-border",
                  activeIndex === allItems.length - 1 ? "bg-muted" : "hover:bg-muted/50",
                )}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(allItems.length - 1)}
                onClick={handleSubmit}
              >
                {resolvedSeeAll}
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function DropdownSection({ label, border, children }: { label: string; border?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider", border && "border-t border-border")}>
        {label}
      </div>
      {children}
    </div>
  );
}

function DropdownRow({
  name,
  subtitle,
  logoUrl,
  icon,
  active,
  onMouseEnter,
  onClick,
}: {
  name: string;
  subtitle?: string;
  logoUrl?: string | null;
  icon?: React.ReactNode;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2 transition-colors text-left",
        active ? "bg-muted" : "hover:bg-muted/50",
      )}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {logoUrl ? (
        <div className="relative w-8 h-8 shrink-0 bg-muted rounded-md overflow-hidden">
          <Image src={logoUrl} alt={name} fill sizes="32px" className="object-contain p-0.5" />
        </div>
      ) : icon ? (
        <div className="flex w-8 h-8 shrink-0 items-center justify-center bg-muted rounded-md">
          {icon}
        </div>
      ) : null}
      <div className="min-w-0">
        <span className="text-sm font-medium truncate block">{name}</span>
        {subtitle && <span className="text-xs text-muted-foreground truncate block">{subtitle}</span>}
      </div>
    </button>
  );
}
